import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  // Convert number to string and handle decimals
  const numStr = Math.abs(amount).toFixed(0);
  
  // Split integer into parts based on Indian numbering system
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNums = numStr.substring(0, numStr.length - 3);
  
  // Add commas according to Indian number system
  const formatted = otherNums !== '' 
    ? otherNums.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return '₹' + (amount < 0 ? '-' : '') + formatted;
};

const TopupCalculator = () => {
  const [values, setValues] = useState({
    existingLoanAmount: 1000000,
    existingEMI: 12000,
    existingTenureLeft: 5,
    existingInterestRate: 8.5,
    topUpAmount: 500000,
    topUpInterestRate: 9,
    newLoanTenure: 10,
    processingFee: 1
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showToolTip, setShowToolTip] = useState('');
  
  const handleChange = (key, value) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateLoan = () => {
    const { 
      existingLoanAmount, 
      existingEMI, 
      existingTenureLeft,
      existingInterestRate,
      topUpAmount, 
      topUpInterestRate, 
      newLoanTenure,
      processingFee 
    } = values;

    // Calculate outstanding principal of existing loan
    const monthlyRateExisting = existingInterestRate / (12 * 100);
    const totalMonthsExisting = existingTenureLeft * 12;
    
    // Calculate approximate outstanding principal using EMI formula
    // P = EMI * [(1 - (1 + r)^(-n)) / r]
    const outstandingPrincipal = existingEMI * ((1 - Math.pow(1 + monthlyRateExisting, -totalMonthsExisting)) / monthlyRateExisting);
    
    // Total new loan amount (outstanding + top-up)
    const totalNewLoanAmount = outstandingPrincipal + topUpAmount;
    
    // Calculate processing fee on top-up amount
    const processingFeeAmount = (topUpAmount * processingFee) / 100;
    
    // Monthly interest rate for new loan
    const monthlyRateNew = topUpInterestRate / (12 * 100);
    
    // Total number of monthly payments for new loan
    const totalMonthsNew = newLoanTenure * 12;
    
    // Calculate EMI for new loan using formula: P * r * (1 + r)^n / ((1 + r)^n - 1)
    const numerator = totalNewLoanAmount * monthlyRateNew * Math.pow(1 + monthlyRateNew, totalMonthsNew);
    const denominator = Math.pow(1 + monthlyRateNew, totalMonthsNew) - 1;
    const newEMI = numerator / denominator;
    
    // Validate EMI calculation
    if (!isFinite(newEMI) || isNaN(newEMI)) {
      return {
        outstandingPrincipal: Math.round(outstandingPrincipal),
        newEMI: 0,
        totalNewLoanAmount: Math.round(totalNewLoanAmount),
        processingFeeAmount: Math.round(processingFeeAmount),
        emiDifference: -existingEMI,
        totalInterestNewLoan: 0,
        totalAmountPayable: Math.round(totalNewLoanAmount) + Math.round(processingFeeAmount),
        totalSavingsOrAdditionalCost: 0
      };
    }
    
    // Calculate difference in EMI
    const emiDifference = newEMI - existingEMI;
    
    // Calculate total interest for new loan
    const totalInterestNewLoan = (newEMI * totalMonthsNew) - totalNewLoanAmount;
    
    // Calculate total amount payable
    const totalAmountPayable = (newEMI * totalMonthsNew) + processingFeeAmount;
    
    // Calculate if the top-up is saving money or costing more
    const remainingPaymentsOldLoan = existingEMI * totalMonthsExisting;
    const newPayments = totalAmountPayable;
    
    // If new payments is less than remaining + top-up, it's a saving, otherwise it's a cost
    const totalSavingsOrAdditionalCost = (remainingPaymentsOldLoan + topUpAmount) - newPayments;
    
    return {
      outstandingPrincipal: Math.round(outstandingPrincipal),
      newEMI: Math.round(newEMI),
      totalNewLoanAmount: Math.round(totalNewLoanAmount),
      processingFeeAmount: Math.round(processingFeeAmount),
      emiDifference: Math.round(emiDifference),
      totalInterestNewLoan: Math.round(totalInterestNewLoan),
      totalAmountPayable: Math.round(totalAmountPayable),
      totalSavingsOrAdditionalCost: Math.round(totalSavingsOrAdditionalCost)
    };
  };

  const [result, setResult] = useState(calculateLoan());

  useEffect(() => {
    setResult(calculateLoan());
  }, [values]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Top Up Loan Calculator</h1>
        <h2 className="text-lg text-gray-600">Calculate how much extra you can borrow on your existing loan</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Existing Loan Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Existing Loan Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current Loan Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('existingLoanAmount')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'existingLoanAmount' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Original principal amount of your existing loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.existingLoanAmount}
                    onChange={(e) => handleChange('existingLoanAmount', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={100000}
                    max={10000000}
                    step={50000}
                    value={values.existingLoanAmount}
                    onChange={(e) => handleChange('existingLoanAmount', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1L</span>
                    <span>₹1Cr</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current EMI
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('existingEMI')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'existingEMI' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Your current monthly EMI payment
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.existingEMI}
                    onChange={(e) => handleChange('existingEMI', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1000}
                    max={200000}
                    step={1000}
                    value={values.existingEMI}
                    onChange={(e) => handleChange('existingEMI', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1K</span>
                    <span>₹2L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Remaining Tenure (Years)</label>
                  <input
                    type="number"
                    value={values.existingTenureLeft}
                    onChange={(e) => handleChange('existingTenureLeft', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={1}
                    value={values.existingTenureLeft}
                    onChange={(e) => handleChange('existingTenureLeft', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>20 years</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current Interest Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('existingInterestRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'existingInterestRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Current annual interest rate on your loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.existingInterestRate}
                    onChange={(e) => handleChange('existingInterestRate', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={5}
                    max={15}
                    step={0.1}
                    value={values.existingInterestRate}
                    onChange={(e) => handleChange('existingInterestRate', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5%</span>
                    <span>15%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Up Loan Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Top Up Loan Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Top Up Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('topUpAmount')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'topUpAmount' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Additional amount you want to borrow
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.topUpAmount}
                    onChange={(e) => handleChange('topUpAmount', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={50000}
                    max={5000000}
                    step={50000}
                    value={values.topUpAmount}
                    onChange={(e) => handleChange('topUpAmount', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹50K</span>
                    <span>₹50L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    New Interest Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('topUpInterestRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'topUpInterestRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Interest rate for the combined loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.topUpInterestRate}
                    onChange={(e) => handleChange('topUpInterestRate', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={5}
                    max={15}
                    step={0.1}
                    value={values.topUpInterestRate}
                    onChange={(e) => handleChange('topUpInterestRate', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5%</span>
                    <span>15%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">New Loan Tenure (Years)</label>
                  <input
                    type="number"
                    value={values.newLoanTenure}
                    onChange={(e) => handleChange('newLoanTenure', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={values.newLoanTenure}
                    onChange={(e) => handleChange('newLoanTenure', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>30 years</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Processing Fee
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('processingFee')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'processingFee' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Processing fee percentage for the top-up amount
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.processingFee}
                    onChange={(e) => handleChange('processingFee', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={3}
                    step={0.1}
                    value={values.processingFee}
                    onChange={(e) => handleChange('processingFee', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>3%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">New Monthly Payment</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.newEMI)}
                  </div>
                  <div className="text-sm text-gray-300">New Monthly EMI</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Outstanding Principal</span>
                    <span className="font-bold">{formatCurrency(result.outstandingPrincipal)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Top Up Amount</span>
                    <span className="font-bold">{formatCurrency(values.topUpAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">New Loan Amount</span>
                    <span className="font-bold">{formatCurrency(result.totalNewLoanAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Processing Fee</span>
                    <span className="font-bold">{formatCurrency(result.processingFeeAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">EMI Difference</span>
                    <span className="font-bold">{formatCurrency(result.emiDifference)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Interest</span>
                    <span className="font-bold">{formatCurrency(result.totalInterestNewLoan)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Amount Payable</span>
                    <span className="font-bold">{formatCurrency(result.totalAmountPayable)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Apply for Top Up Loan →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopupCalculator;